import React, { useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Stack,
  Divider,
  alpha,
} from '@mui/material';
import { OrderPaymentStatus } from '../../types';
import { PaymentStatusChip } from './PaymentStatusChip';
import { palette, typography, theme as themeConfig } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';

interface PaymentSummaryCardProps {
  /** Current payment status of the order */
  paymentStatus: OrderPaymentStatus;
  /** Total amount due for the order */
  totalAmount: number;
  /** Cumulative amount paid */
  paidAmount: number;
  /** Amount that has been refunded (optional) */
  refundedAmount?: number;
  /** Animation delay for slide-up effect (optional) */
  animationDelay?: number;
}

/**
 * Get the appropriate color for the progress bar based on payment percentage
 */
const getProgressColor = (percentage: number, status: OrderPaymentStatus): string => {
  if (status === OrderPaymentStatus.REFUNDED_FULL || status === OrderPaymentStatus.REFUNDED_PARTIAL) {
    return palette.info;
  }
  if (percentage >= 100) {
    return palette.success;
  }
  if (percentage > 0) {
    return palette.warning;
  }
  return palette.error;
};

/**
 * Get the color for the remaining amount based on payment status
 */
const getRemainingColor = (remaining: number, status: OrderPaymentStatus): string => {
  if (status === OrderPaymentStatus.PAID) {
    return palette.success;
  }
  if (remaining > 0) {
    return palette.warning;
  }
  return palette.textMuted;
};

/**
 * Format currency in DZD (Algerian Dinar)
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
  }).format(amount);
};

export const PaymentSummaryCard: React.FC<PaymentSummaryCardProps> = ({
  paymentStatus,
  totalAmount,
  paidAmount,
  refundedAmount = 0,
  animationDelay = 0.1,
}) => {
  const { mode } = useContext(ColorModeContext);

  // Theme-aware colors
  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  // Calculate payment progress
  const effectiveTotal = Math.max(totalAmount, 0.01); // Prevent division by zero
  const effectivePaid = Math.max(paidAmount, 0);
  const percentage = Math.min((effectivePaid / effectiveTotal) * 100, 100);

  // Calculate remaining balance (accounting for refunds)
  const remaining = Math.max(totalAmount - effectivePaid - refundedAmount, 0);

  // Get colors based on status and progress
  const progressColor = getProgressColor(percentage, paymentStatus);
  const remainingColor = getRemainingColor(remaining, paymentStatus);

  return (
    <Card
      sx={{
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        '@keyframes slideUp': {
          from: {
            opacity: 0,
            transform: 'translateY(10px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        animation: `slideUp 0.4s ease-out ${animationDelay}s both`,
      }}
    >
      <CardContent>
        {/* Header with title and status chip */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Statut du Paiement
          </Typography>
          <PaymentStatusChip status={paymentStatus} size="medium" />
        </Box>

        {/* Progress bar with percentage */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: typography.fontFamily.mono }}
            >
              Progression
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                fontFamily: typography.fontFamily.mono,
                color: progressColor,
              }}
            >
              {percentage.toFixed(0)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 10,
              borderRadius: themeConfig.radius.sm,
              bgcolor: alpha(progressColor, 0.15),
              '& .MuiLinearProgress-bar': {
                bgcolor: progressColor,
                borderRadius: themeConfig.radius.sm,
                transition: 'transform 0.6s ease-in-out',
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 2, borderColor: colors.border }} />

        {/* Amount breakdown */}
        <Stack spacing={2}>
          {/* Total amount */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ fontFamily: typography.fontFamily.mono }}
            >
              {formatCurrency(totalAmount)}
            </Typography>
          </Box>

          {/* Amount paid */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Payé
            </Typography>
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{
                fontFamily: typography.fontFamily.mono,
                color: effectivePaid > 0 ? palette.success : colors.textSecondary,
              }}
            >
              {formatCurrency(effectivePaid)}
            </Typography>
          </Box>

          {/* Refunded amount (only show if applicable) */}
          {refundedAmount > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Remboursé
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  color: palette.error,
                }}
              >
                -{formatCurrency(refundedAmount)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 0.5, borderColor: colors.border }} />

          {/* Remaining balance */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              bgcolor: alpha(remainingColor, 0.08),
              p: 1.5,
              borderRadius: themeConfig.radius.md,
              border: `1px solid ${alpha(remainingColor, 0.2)}`,
            }}
          >
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ fontFamily: typography.fontFamily.display }}
            >
              Restant
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                fontFamily: typography.fontFamily.mono,
                color: remainingColor,
              }}
            >
              {formatCurrency(remaining)}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PaymentSummaryCard;
